import {useEffect, useMemo, useState} from "react"
import {createUseStyles} from 'react-jss'
import ConfirmModal from "../components/ConfirmModal"
import FullScreenLoadingIndicator from "../components/FullScreenLoadingIndicator"
import useCustomerPortalUserActions from "../hooks/useCustomerPortalUserActions"
import {CreateOrUpdateCustomerPortalRoleModel, CustomerPortalRoleModel} from "../redux/types"
import Button from '../components/Button'
import {useAppSelector} from "../redux/hooks"
import Card from "../components/Card"
import {useTranslation} from "react-i18next"
import useCustomerPortalRolesActions from "../hooks/useCustomerPortalRolesActions";
import CustomerPortalRoleCard from "../components/PortalRoles/CustomerPortalRoleCard";
import EditCustomerPortalRoleModal from "../components/PortalRoles/EditCustomerPortalRoleModal";

const CustomerPortalRoles = () => {
  const styles = useStyles()
  const {t} = useTranslation('portalRoles');

  const [confirmDeleteModalVisible, setConfirmDeleteModalVisible] = useState<boolean>(false)
  const [deleteCandidate, setDeleteCandidate] = useState<CustomerPortalRoleModel>()

  const [editModalVisible, setEditModalVisible] = useState<boolean>(false)
  const [editPortalRoleModel, setEditPortalRoleModel] = useState<CustomerPortalRoleModel>()

  const {getUsers} = useCustomerPortalUserActions()
  const usersLoading = useAppSelector(state => state.customerPortalUser.loading)
  const portalUsers = useAppSelector(state => state.customerPortalUser.portalUsers)

  const portalRoles = useAppSelector(state => state.customerPortalRoles.portalRoles)
  const rolesLoading = useAppSelector(state => state.customerPortalRoles.loading)
  const {getRoles, createRole, updateRole, deleteRole} = useCustomerPortalRolesActions()

  const sortedPortalRoles = useMemo(() => [...portalRoles].sort((a, b) => a.name.localeCompare(b.name)), [portalRoles])

  useEffect(() => {
    if (portalUsers.length === 0) {
      getUsers()
    }
  }, [])

  useEffect(() => {
    if (portalRoles.length === 0) {
      getRoles()
    }
  }, []);

  const onAddClick = () => {
    setEditPortalRoleModel(undefined)
    setEditModalVisible(true)
  }

  const onEditClick = (portalRole: CustomerPortalRoleModel) => {
    setEditPortalRoleModel(portalRole)
    setEditModalVisible(true)
  }

  const handlePortalRoleEdited = (createOrUpdatePortalRoleModel: CreateOrUpdateCustomerPortalRoleModel) => {
    if (editPortalRoleModel) {
      updateRole(editPortalRoleModel.id, createOrUpdatePortalRoleModel)
      setEditPortalRoleModel(undefined)
      setEditModalVisible(false)
    }
    else {
      createRole(createOrUpdatePortalRoleModel)
      setEditModalVisible(false)
    }
  }

  const handlePortalRoleEditCancelled = () => {
    setEditPortalRoleModel(undefined)
    setEditModalVisible(false)
  }

  const onDeleteClick = (portalRole: CustomerPortalRoleModel) => {
    setDeleteCandidate(portalRole)
    setConfirmDeleteModalVisible(true)
  }

  const handleDeleteConfirmed = () => {
    if (deleteCandidate) {
      deleteRole(deleteCandidate.id)
      setDeleteCandidate(undefined)
    }
    setConfirmDeleteModalVisible(false)
  }

  return (
    <div className={styles.container}>
      <Card>
        <h2>
          {t('title')}
        </h2>
      </Card>

      <Card className={styles.buttonCard}>
        <Button onClick={onAddClick} title={t('addPortalRole')}/>
      </Card>

      {sortedPortalRoles.map(role =>
        <CustomerPortalRoleCard
          key={role.id}
          portalRole={role}
          employerPortalUsers={portalUsers}
          onEditClick={onEditClick}
          onDeleteClick={onDeleteClick}
        />
      )}

      <FullScreenLoadingIndicator visible={usersLoading || rolesLoading}/>

      <EditCustomerPortalRoleModal
        portalRole={editPortalRoleModel}
        visible={editModalVisible}
        onConfirmClick={handlePortalRoleEdited}
        onCancelClick={handlePortalRoleEditCancelled}
      />

      <ConfirmModal
        visible={confirmDeleteModalVisible}
        confirmText={t('confirmDeletePortalRole', {ns: 'portalRoles'})}
        onConfirmClick={handleDeleteConfirmed}
        onCancelClick={() => setConfirmDeleteModalVisible(false)}
      />
    </div>
  )
}

export default CustomerPortalRoles

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20
  },
  buttonCard: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
  }
})
